import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { apiClient } from '../services/api';

/** BE `BidSummaryBody` 와 같은 모양 */
interface SummaryBody {
  overview: string;
  scope: string[];
  period: string;
  budget: string;
  eligibility: string[];
  evaluation: string[];
  submission: string[];
  cautions: string[];
}

type Status =
  | { state: 'none' }
  | { state: 'pending' }
  | { state: 'failed'; error: string }
  | { state: 'done'; summary: SummaryBody; files: { name: string; type: string }[]; model: string | null; createdAt: string };

/** 요약은 30초~1분 걸린다. 그동안 몇 초마다 본다 */
const POLL_MS = 3000;

/**
 * 공고 상세 맨 위의 요약. 두 층이다.
 *
 * - **한눈 요약** — 공고에 이미 있는 값으로 만든 칩. 바로 보이고 비용이 없다
 * - **AI 요약** — 첨부(제안요청서 · 과업지시서 · 공고문)를 읽고 쓴 것. 공고 하나에
 *   한 번만 만들고 저장해 둔다 — 한 사람이 만들면 모두에게 계속 보인다
 */
export const BidSummaryPanel: React.FC<{
  bidNtceNo: string;
  facts: string[];
  /** 마감돼 원본이 지워진 공고 — 만들 수 없다(서버가 원본을 읽는다). 이미 있으면 보여만 준다 */
  readOnly?: boolean;
  /** 요약이 새로 끝났을 때(만드는 중 → 완료). 처음부터 있던 요약에는 부르지 않는다 */
  onDone?: () => void;
}> = ({ bidNtceNo, facts, readOnly = false, onDone }) => {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState('');
  const url = `/bidding/summary/${encodeURIComponent(bidNtceNo)}`;

  const load = useCallback(async () => {
    try {
      setStatus(await apiClient.get(url));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [url]);

  useEffect(() => {
    setStatus(null);
    setError('');
    load();
  }, [load]);

  // 만드는 중이면 끝날 때까지 본다
  useEffect(() => {
    if (status?.state !== 'pending') return;
    const t = setTimeout(load, POLL_MS);
    return () => clearTimeout(t);
  }, [status, load]);

  /* 만드는 중이던 요약이 끝나면 알린다 — 검토 탭이 접수서 빈 칸을 채운다 */
  const wasPending = useRef(false);
  useEffect(() => {
    if (status?.state === 'pending') wasPending.current = true;
    else if (status?.state === 'done' && wasPending.current) { wasPending.current = false; onDone?.(); }
  }, [status, onDone]);

  /* 누르고 응답이 오기 전 한 번 더 눌리지 않게(#113) */
  const [starting, setStarting] = useState(false);
  const start = async () => {
    if (starting) return;
    setStarting(true);
    setError('');
    try {
      setStatus(await apiClient.post(url, {}));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="space-y-3">
      {facts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {facts.map((f) => (
            <span key={f} className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-xs">{f}</span>
          ))}
        </div>
      )}

      {!(readOnly && status?.state !== 'done' && status?.state !== 'pending') && (
      <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700">
            <Sparkles size={14} /> AI 요약
          </h4>
          {status?.state === 'done' && (
            <span className="text-[11px] text-slate-400">
              첨부 {status.files.length}개를 읽음 · {new Date(status.createdAt).toLocaleDateString('sv-SE')}
            </span>
          )}
        </div>

        {status === null && !error && <p className="mt-2 text-xs text-slate-400">불러오는 중…</p>}

        {(status?.state === 'none' || status?.state === 'failed') && (
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              {status.state === 'failed'
                ? `만들지 못했습니다 — ${status.error}`
                : '제안요청서 · 과업지시서를 읽고 요약합니다. 공고당 한 번 만들면 모두에게 계속 보입니다.'}
            </p>
            <button
              onClick={start}
              disabled={starting}
              className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700"
            >
              {status.state === 'failed' ? '다시 만들기' : '요약 만들기'}
            </button>
          </div>
        )}

        {status?.state === 'pending' && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
            <Loader2 size={14} className="animate-spin" /> 첨부를 읽고 있습니다 — 30초~1분 걸립니다
          </p>
        )}

        {status?.state === 'done' && <SummaryView s={status.summary} />}

        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      </div>
      )}
    </div>
  );
};

const SummaryView: React.FC<{ s: SummaryBody }> = ({ s }) => (
  <div className="mt-2 space-y-3 text-sm text-slate-800">
    {s.overview && <p className="leading-relaxed">{s.overview}</p>}
    {(s.period || s.budget) && (
      <dl className="grid grid-cols-[4rem_1fr] gap-x-3 gap-y-1 text-xs">
        {s.period && (<><dt className="text-slate-500">기간</dt><dd>{s.period}</dd></>)}
        {s.budget && (<><dt className="text-slate-500">사업비</dt><dd>{s.budget}</dd></>)}
      </dl>
    )}
    <SummaryList title="주요 과업" items={s.scope} />
    <SummaryList title="참가 자격" items={s.eligibility} />
    <SummaryList title="평가 · 낙찰" items={s.evaluation} />
    <SummaryList title="제출" items={s.submission} />
    <SummaryList title="유의사항" items={s.cautions} warn />
  </div>
);

const SummaryList: React.FC<{ title: string; items: string[]; warn?: boolean }> = ({ title, items, warn }) =>
  items?.length ? (
    <div>
      <h5 className={`text-xs font-semibold mb-1 ${warn ? 'text-amber-700' : 'text-slate-500'}`}>{title}</h5>
      <ul className="list-disc pl-4 space-y-0.5 text-xs leading-relaxed">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  ) : null;
